import React from 'react';
import DocLayout from '../DocLayout';
import { Target, TrendingUp, Award } from 'lucide-react';

const Scoring = () => {
  return (
    <DocLayout title="Lead Scoring System" category="Lead Management">
      <div className="space-y-6">
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Award className="w-6 h-6 text-indigo-600" />
            AI-Powered Lead Scoring
          </h2>
          <p className="text-slate-700 mb-4">
            I-Match automatically scores your leads from 0-100 based on data completeness, enrichment quality, and engagement indicators.
          </p>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">What is Lead Scoring?</h3>
          <p className="text-slate-700 mb-3">
            Lead scoring is an automated system that ranks your leads based on how well they match your ideal customer profile and their likelihood to convert.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-slate-700">
              <strong>Higher scores = Higher priority:</strong> Leads with scores above 70 should be prioritized for immediate follow-up.
            </p>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Scoring Components</h3>
          <p className="text-slate-700 mb-3">
            Lead scores are calculated based on four main factors:
          </p>
          
          <div className="space-y-3">
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-slate-900">1. Data Completeness (40 points)</h4>
                <span className="text-indigo-600 font-bold">40%</span>
              </div>
              <p className="text-slate-600 text-sm mb-2">
                How much contact and company information is filled in:
              </p>
              <ul className="list-disc pl-6 text-slate-600 text-sm space-y-1">
                <li>Name, email, phone: 10 points</li>
                <li>Company, title, website: 10 points</li>
                <li>Additional contact details: 10 points</li>
                <li>Company details (size, revenue): 10 points</li>
              </ul>
            </div>

            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-slate-900">2. Enrichment Quality (20 points)</h4>
                <span className="text-purple-600 font-bold">20%</span>
              </div>
              <p className="text-slate-600 text-sm mb-2">
                Quality and depth of AI-enriched data:
              </p>
              <ul className="list-disc pl-6 text-slate-600 text-sm space-y-1">
                <li>Has enriched data: 10 points</li>
                <li>High AI confidence score: +5 points</li>
                <li>Comprehensive analysis: +5 points</li>
              </ul>
            </div>

            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-slate-900">3. Pipeline Stage (20 points)</h4>
                <span className="text-green-600 font-bold">20%</span>
              </div>
              <p className="text-slate-600 text-sm mb-2">
                Current position in sales pipeline:
              </p>
              <ul className="list-disc pl-6 text-slate-600 text-sm space-y-1">
                <li>New: 5 points</li>
                <li>Contacted: 8 points</li>
                <li>Qualified: 12 points</li>
                <li>Proposal: 16 points</li>
                <li>Negotiation: 20 points</li>
              </ul>
            </div>

            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-slate-900">4. Engagement & Relationships (20 points)</h4>
                <span className="text-orange-600 font-bold">20%</span>
              </div>
              <p className="text-slate-600 text-sm mb-2">
                Interactions and connections:
              </p>
              <ul className="list-disc pl-6 text-slate-600 text-sm space-y-1">
                <li>Has assigned owner: 5 points</li>
                <li>Linked contacts: 5 points</li>
                <li>Company relationship: 5 points</li>
                <li>Activity history: 5 points</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Score Interpretation</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <span className="px-3 py-1 bg-green-600 text-white font-bold rounded">80-100</span>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Hot Lead 🔥</p>
                <p className="text-slate-600 text-xs">High priority - Contact immediately</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="px-3 py-1 bg-blue-600 text-white font-bold rounded">60-79</span>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Warm Lead 🔶</p>
                <p className="text-slate-600 text-xs">Good potential - Follow up soon</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <span className="px-3 py-1 bg-yellow-600 text-white font-bold rounded">40-59</span>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Cool Lead 💧</p>
                <p className="text-slate-600 text-xs">Needs nurturing - Add to campaign</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <span className="px-3 py-1 bg-gray-600 text-white font-bold rounded">0-39</span>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Cold Lead ❄️</p>
                <p className="text-slate-600 text-xs">Low priority - Enrich data first</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">How to Improve Lead Scores</h3>
          <ol className="list-decimal pl-6 space-y-3 text-slate-700">
            <li>
              <strong>Run AI Enrichment</strong>
              <p className="text-sm text-slate-600 mt-1">Use the "Enrich" button to fill in missing company data automatically</p>
            </li>
            <li>
              <strong>Complete Contact Information</strong>
              <p className="text-sm text-slate-600 mt-1">Add phone numbers, titles, and other missing details</p>
            </li>
            <li>
              <strong>Move Leads Forward</strong>
              <p className="text-sm text-slate-600 mt-1">Update stage as you progress through the sales process</p>
            </li>
            <li>
              <strong>Assign Owners</strong>
              <p className="text-sm text-slate-600 mt-1">Assign each lead to a responsible team member</p>
            </li>
            <li>
              <strong>Link Related Records</strong>
              <p className="text-sm text-slate-600 mt-1">Connect contacts and companies for relationship tracking</p>
            </li>
          </ol>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Bulk Score Recalculation</h3>
          <p className="text-slate-700 mb-3">
            Admin users can recalculate all lead scores at once:
          </p>
          <ol className="list-decimal pl-6 space-y-2 text-slate-700">
            <li>Navigate to Admin Panel</li>
            <li>Find the "Scoring" section</li>
            <li>Click "Recalculate All Scores"</li>
            <li>Wait for the process to complete</li>
            <li>All leads will have updated scores</li>
          </ol>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-3">
            <p className="text-sm text-slate-700">
              <strong>Note:</strong> Scores are automatically recalculated when leads are updated, so manual recalculation is only needed after system changes.
            </p>
          </div>
        </section>

        <section>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-slate-900 mb-2">💡 Using Scores Effectively</h4>
            <ul className="space-y-1 text-slate-700 text-sm">
              <li>• Sort leads by score to prioritize your daily outreach</li>
              <li>• Focus on leads scoring 70+ for best conversion rates</li>
              <li>• Use low scores to identify leads needing data enrichment</li>
              <li>• Track average score trends over time</li>
              <li>• Set team goals based on score improvements</li>
            </ul>
          </div>
        </section>
      </div>
    </DocLayout>
  );
};

export default Scoring;